import { useMemo, useState } from 'react'
import swishQrcodeSvg from 'swish-qrcode-svg'
import SwishLogo from '@/components/ui/SwishLogo'
import { gql } from '@/lib/api'
import { t } from '@/lib/i18n'
import { CANCEL_PAYMENT } from '@/lib/queries'
import { useKioskStore } from '@/stores/kiosk-store'

export default function PayingScreen() {
  const payMethod = useKioskStore((s) => s.payMethod)
  const swishQr = useKioskStore((s) => s.swishQr)
  const config = useKioskStore((s) => s.config)
  const parseToken = useKioskStore((s) => s.parseToken)
  const locale = useKioskStore((s) => s.locale)
  const setStep = useKioskStore((s) => s.setStep)
  const setSwishQr = useKioskStore((s) => s.setSwishQr)
  const setProcessing = useKioskStore((s) => s.setProcessing)
  const isLandscape = useKioskStore((s) => s.remoteConfig?.layoutMode === 'landscape')
  const [cancelling, setCancelling] = useState(false)

  // Generate Swish-branded QR SVG with embedded logo, then encode as a data
  // URL so we can render it via <img src> (avoids dangerouslySetInnerHTML).
  const qrSrc = useMemo(() => {
    if (!swishQr) return null
    const svg = swishQrcodeSvg(swishQr, { margin: 2 })
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
  }, [swishQr])

  const handleCancel = async () => {
    if (cancelling) return
    setCancelling(true)

    // 1. Signal abort to polling loop
    const w = window as unknown as {
      __kioskAbort?: { current: boolean }
      __kioskCancelInfo?: { paymentIntentId?: string }
    }
    const abortRef = w.__kioskAbort
    if (abortRef) abortRef.current = true

    // 2. Send cancellation to API — this tells LocoPOS/Stripe to cancel the
    // payment intent, which propagates to the card terminal and stops it
    const info = w.__kioskCancelInfo
    if (info?.paymentIntentId && config && parseToken) {
      try {
        await gql(config.apiEndpoint, CANCEL_PAYMENT, { pid: info.paymentIntentId }, parseToken)
      } catch (e) {
        console.error('Cancel payment failed:', e)
      }
    }

    setSwishQr(null)
    setProcessing(false)
    setCancelling(false)
    setStep('checkout')
  }

  return (
    <div className="h-full w-full flex flex-col items-center justify-center bg-kiosk-bg px-8">
      <div className={`w-full space-y-8 text-center ${isLandscape ? 'max-w-xl' : 'max-w-md'}`}>
        {payMethod === 'swish' ? (
          <>
            {/* Swish logo */}
            <div className="flex justify-center">
              <SwishLogo className="w-20 h-20" />
            </div>
            <h2 className="text-2xl font-bold text-kiosk-text">{t('paying.swishTitle', locale)}</h2>
            <p className="text-kiosk-muted text-lg">{t('paying.swishDesc', locale)}</p>
            {qrSrc ? (
              <div className="flex justify-center">
                <div className="bg-white p-6 rounded-3xl shadow-lg shadow-black/30 inline-block">
                  <img src={qrSrc} alt="Swish QR" className="w-72 h-72 block" />
                </div>
              </div>
            ) : (
              <div className="flex justify-center">
                <div className="kiosk-spinner" />
              </div>
            )}
          </>
        ) : (
          <>
            {/* Card payment — illustrated terminal with accent glow (Baemingo-inspired) */}
            <h2 className="text-4xl font-bold text-kiosk-text">{t('paying.cardTitle', locale)}</h2>
            <p className="text-kiosk-muted text-xl">{t('paying.cardDesc', locale)}</p>

            <div className="relative flex items-center justify-center py-6">
              {/* Ambient glow */}
              <div className="absolute w-72 h-72 rounded-full bg-kiosk-accent/20 blur-3xl animate-pulse" />
              {/* Terminal illustration */}
              <CardTerminalIllustration className="relative w-56 h-72 drop-shadow-2xl" />
            </div>

            <div className="flex items-center justify-center gap-3 pt-2">
              <div className="kiosk-spinner w-6! h-6!" />
              <p className="text-kiosk-text/80 text-base">{locale === 'en' ? 'Payment in progress…' : 'Betalningen pågår…'}</p>
            </div>
          </>
        )}

        <button onClick={handleCancel} disabled={cancelling} className="kiosk-btn w-full py-4 text-kiosk-muted text-base border border-kiosk-surface-2 rounded-2xl mt-8 disabled:opacity-50">
          {cancelling ? (locale === 'en' ? 'Cancelling...' : 'Avbryter...') : `\u2190 ${t('paying.cancel', locale)}`}
        </button>
      </div>
    </div>
  )
}

// ─── Card terminal illustration ───
// Stylized SVG of a card reader with a card slotted in at the bottom.
// Uses currentColor-friendly palette (CSS vars) where possible so it adapts to theme.
function CardTerminalIllustration({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 200 280" xmlns="http://www.w3.org/2000/svg" className={className}>
      <defs>
        <linearGradient id="terminalBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F1F5F9" />
          <stop offset="100%" stopColor="#CBD5E1" />
        </linearGradient>
        <linearGradient id="screenGlow" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--kiosk-accent)" stopOpacity="0.95" />
          <stop offset="100%" stopColor="var(--kiosk-accent)" stopOpacity="0.7" />
        </linearGradient>
        <linearGradient id="cardBody" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#1F2937" />
          <stop offset="100%" stopColor="#374151" />
        </linearGradient>
      </defs>

      {/* Terminal body */}
      <rect x="20" y="10" width="160" height="210" rx="16" fill="url(#terminalBody)" stroke="#94A3B8" strokeWidth="2" />

      {/* Screen */}
      <rect x="36" y="26" width="128" height="50" rx="4" fill="url(#screenGlow)" />
      <rect x="44" y="40" width="60" height="4" rx="2" fill="white" opacity="0.9" />
      <rect x="44" y="50" width="40" height="4" rx="2" fill="white" opacity="0.7" />
      <rect x="44" y="60" width="80" height="4" rx="2" fill="white" opacity="0.5" />

      {/* Button grid — 3×4 */}
      {[0, 1, 2].map((col) =>
        [0, 1, 2, 3].map((row) => {
          const cx = 54 + col * 46
          const cy = 100 + row * 28
          // Bottom row: red (cancel) / yellow (clear) / green (confirm)
          const isBottom = row === 3
          const fill = isBottom ? (col === 0 ? '#EF4444' : col === 1 ? '#FCD34D' : '#22C55E') : '#64748B'
          return <circle key={`${col}-${row}`} cx={cx} cy={cy} r="10" fill={fill} />
        }),
      )}

      {/* Card slot at bottom */}
      <rect x="50" y="220" width="100" height="4" rx="2" fill="#64748B" />

      {/* Inserted card — slight tilt + subtle rise animation */}
      <g transform="translate(60 225) rotate(-2 40 30)">
        <rect x="0" y="0" width="80" height="52" rx="6" fill="url(#cardBody)" stroke="#111827" strokeWidth="1" />
        {/* Chip */}
        <rect x="8" y="10" width="14" height="12" rx="2" fill="#FBBF24" />
        <rect x="10" y="12" width="10" height="8" rx="1" fill="#D97706" opacity="0.5" />
        {/* Card stripe */}
        <rect x="8" y="30" width="44" height="3" rx="1" fill="white" opacity="0.4" />
        <rect x="8" y="36" width="30" height="3" rx="1" fill="white" opacity="0.3" />
      </g>
    </svg>
  )
}
